import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, CardHeader, Switch, Spinner } from '@nextui-org/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { FaChartBar } from 'react-icons/fa6';
import Icon from '../base/Icon';
import { apiKeyService } from '../../services/apiKeyService';
import { DateRange } from './DateRangeSelector';

interface EndpointBreakdownChartProps {
  className?: string;
  dateRange?: DateRange;
  apiKeyId?: string;
}

interface EndpointBreakdownData {
  endpoint: string;
  requests: number;
}

const EndpointBreakdownChart: React.FC<EndpointBreakdownChartProps> = ({
  className = '',
  dateRange,
  apiKeyId,
}) => {
  const [breakdownData, setBreakdownData] = useState<EndpointBreakdownData[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAsPercentage, setShowAsPercentage] = useState(false);

  const loadBreakdownData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiKeyService.getUsageBreakdown(apiKeyId);
      const rawData: EndpointBreakdownData[] = (response.breakdown || []).map(
        (item: any) => ({
          endpoint: item.endpoint,
          requests: Number(item.requests) || 0,
        })
      );
      setBreakdownData(rawData);
    } catch (err) {
      console.error('Failed to load endpoint breakdown data:', err);
      setError('Failed to load endpoint breakdown data');
      setBreakdownData([]);
    } finally {
      setLoading(false);
    }
  }, [apiKeyId]);

  useEffect(() => {
    loadBreakdownData();
  }, [loadBreakdownData]);

  const totalRequests = breakdownData.reduce(
    (sum, item) => sum + item.requests,
    0
  );

  const getChartData = () => {
    if (!showAsPercentage || totalRequests === 0) {
      return breakdownData;
    }
    return breakdownData.map((item) => ({
      endpoint: item.endpoint,
      requests: Number(((item.requests / totalRequests) * 100).toFixed(1)),
    }));
  };

  const chartData = getChartData();

  const formatTooltip = (value: any) => {
    if (showAsPercentage) return [`${value}%`, 'Requests'];
    return [Number(value).toLocaleString(), 'Requests'];
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <h3 className="text-lg font-semibold">Usage by Endpoint</h3>
        </CardHeader>
        <CardBody className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex justify-between items-center w-full">
          <h3 className="text-lg font-semibold">Usage by Endpoint</h3>
          <div className="flex items-center gap-2">
            <Switch
              size="sm"
              isSelected={showAsPercentage}
              onValueChange={setShowAsPercentage}
            />
            <span className="text-sm text-gray-600">Show as %</span>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {error ? (
          <div className="text-center text-red-500 py-8">
            <p>{error}</p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Icon icon={FaChartBar} className="w-12 h-12 mb-4 text-gray-300" />
            <div className="text-center">
              <p className="text-sm font-medium mb-2">
                No endpoint usage data available
              </p>
              <p className="text-xs text-gray-400">
                Make some API calls to see usage breakdown by endpoint
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="h-96 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="endpoint"
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    width={60}
                    label={{
                      value: showAsPercentage
                        ? 'Percentage (%)'
                        : 'Number of Requests',
                      angle: -90,
                      position: 'insideLeft',
                      offset: 10,
                      dy: 60,
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '12px',
                    }}
                    formatter={formatTooltip}
                  />
                  <Bar dataKey="requests" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {chartData.length > 0 && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div className="text-center p-2 bg-gray-50 rounded">
                  <p className="text-gray-500">Total Endpoints</p>
                  <p className="font-semibold">{chartData.length}</p>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded">
                  <p className="text-gray-500">Total Requests</p>
                  <p className="font-semibold">
                    {totalRequests.toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded">
                  <p className="text-gray-500">Most Used Endpoint</p>
                  <p className="font-semibold">
                    {breakdownData.length > 0
                      ? breakdownData[0].endpoint.substring(0, 12) +
                        (breakdownData[0].endpoint.length > 12 ? '...' : '')
                      : 'N/A'}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default EndpointBreakdownChart;
